import { I18n } from "@lingui/core";
import { Trans } from "@lingui/macro";
import { Heading, Text } from "@otrium/atoms";
import { Locale } from "src/lib/Intl";
import Link from "next/link";
import { RegisterForm } from "src/molecules/RegisterForm";
import { googleTagManager } from "src/tracking";
import { getI18nRoute } from "src/utils/i18nRoutes";
import styles from "../LoginOrRegisterView.module.scss";
import { useCallback } from "react";
import { SocialButtons } from "./SocialButtons";

type RegisterProps = {
  isCheckoutPath?: boolean;
  i18n: I18n;
  locale: Locale;
  signInUrl: string;
  handleLoginClick: () => void;
  handleContinueAsGuest?: () => void;
};

function Register({
  isCheckoutPath,
  i18n,
  locale,
  signInUrl,
  handleLoginClick,
  handleContinueAsGuest,
}: RegisterProps): JSX.Element {
  const onLoginClick = useCallback(() => {
    handleLoginClick();
    googleTagManager.trackAlreadyRegistered();
  }, [handleLoginClick]);

  return (
    <>
      <section className={styles.header}>
        <Heading
          data-testid="login-page-heading"
          as="h1"
          color={"tone.black"}
          fontSize={["32px", "32px", "32px", "48px"]}
          fontFamily="PlusJakartaSans"
          lineHeight={["40px", "40px", "40px", "58px"]}
          sx={{
            textTransform: "none",
          }}
        >
          <Trans>Sign up</Trans>
        </Heading>
        <Text mt={3} fontSize="16px" lineHeight="24px">
          <Trans>
            Use code <strong>WELCOME30</strong> to get 30% off + free shipping
            on your first order.
          </Trans>
        </Text>
      </section>

      <SocialButtons isCheckoutPath={isCheckoutPath} i18n={i18n} />

      <div className={styles.divider}>
        <Text as="span" color="tone.black">
          <Trans>or</Trans>
        </Text>
      </div>

      <RegisterForm
        isCheckoutPath={isCheckoutPath}
        onContinueAsGuest={handleContinueAsGuest}
      />

      <Trans>
        <Text mt={1} fontSize={0} color="tone.anthracite" lineHeight="1.43">
          By signing up, you agree with our{" "}
          <a
            href={getI18nRoute({
              route: "/terms-and-conditions",
              locale,
            })}
            style={{ color: "inherit", textDecoration: "underline" }}
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms & Conditions
          </a>
        </Text>
      </Trans>

      <section className={styles.mobileTabSwitch}>
        <Text
          as="span"
          display="flex"
          placeItems="center"
          justifyContent="center"
          gap={8}
          color="tone.black"
          fontSize={1}
          lineHeight="1.5"
        >
          <Trans>Already registered? </Trans>
          <Link href={signInUrl} onClick={onLoginClick}>
            <Text
              color="Black"
              fontWeight="600"
              sx={{
                textDecoration: "underline",
                ":hover": { cursor: "pointer" },
              }}
            >
              <Trans>Log in</Trans>
            </Text>
          </Link>
        </Text>
      </section>
    </>
  );
}

export { Register };
